import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SaveSearchModel } from '../../../core/models/save-search.model';
import { RequestBodyService } from '../../services/request-body.service';
import { SavedSearches } from '../../services/saved-searches.service';
import { CommonUtilService } from '../../../shared/services/CommonUtil.service';
import { cloneDeep } from 'lodash-es';
import { Lists } from '../../services/lists.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MakeListsPublicPromptComponent } from '../make-lists-public-prompt/make-lists-public-prompt.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SaveSearchApiService, TagsApiService } from '../../services/api';
import { Observable, of } from 'rxjs';
import { LoadingService, TagService } from '../../services';
import { SavedSearchCreateUpdateResponse, SaveSearchPayload, SaveTagDto, TagsDto } from '../../dto';
import { CREATE_TAG_LOADING_TAG, ModalNavigationEnum } from '../../constants';
import { Router } from '@angular/router';
import { FeatureFlagsEnum, PredictiveIntentAutomationEnum } from '../../enums';
import { VerticalMenuTabsEnum } from '../../../modals/icp-automations';
import { ICP_AUTOMATION_SAVED_SEARCH_ID, ICP_VERTICAL_TAB_PARAM, ICP_HORIZONTAL_TAB_PARAM } from '../../../modals/icp-automations';
import { SearchService } from 'src/app/results/search/services/search.service';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { take, switchMap, tap } from 'rxjs/operators';
import { ResultTabsetUrlsEnum } from 'src/app/shared/enums/result-tabset-urls.enum';
import { AW_SELECTED_SIGNAL_PARAM, AW_SELECTED_SAVED_SEARCH_PARAM, AW_START_CONSTRUCTOR_PARAM } from 'src/app/results/agentic-workflows/constants/deeplink.constants';
import { AgenticWorkflowsSignalsEnum } from 'src/app/results/agentic-workflows/enums/agentic-workflows-signals.enum';
import { environment } from 'src/environments/environment';
import { RequestBodyCompany, RequestBodyContact } from '../../../core/models/request-body.model';
import { UniversalSearchRequestModelInterface } from '../../../core/models/universal-search-request.model.interface';
import { CommonModule } from '@angular/common';
import { SiModalModule } from '../si-modal';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MultiSelectorFormFieldModule } from '../ui-components';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { IsFeatureFlagEnabledPipeModule } from '../../pipes';
import { MatInputModule } from '@angular/material/input';
import { DisableAutocompleteDirectiveModule } from '../../directives';
import { MakeListsPublicPromptSaveSearchModule } from '../make-lists-public-prompt';
export const LOADING_TAG: string = 'app-save-search-loader-tag';
const ICP_SIGNAL_KEY: keyof typeof AgenticWorkflowsSignalsEnum = 'ICP';

interface SaveSearchDialogData {
    header?: string;
    type: string;
    searchCriteria?: UniversalSearchRequestModelInterface;
}

@UntilDestroy()
@Component({
    selector: 'app-save-search',
    templateUrl: './save-search.component.html',
    styleUrls: ['./save-search.component.scss'],
    imports: [
      CommonModule,
      SiModalModule,
      MatFormFieldModule,
      MatSelectModule,
      MultiSelectorFormFieldModule,
      FormsModule,
      ReactiveFormsModule,
      MatCheckboxModule,
      MatTooltipModule,
      MatSlideToggleModule,
      MatButtonModule,
      IsFeatureFlagEnabledPipeModule,
      MatInputModule,
      DisableAutocompleteDirectiveModule,
      MakeListsPublicPromptSaveSearchModule,
      MatDialogModule
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveSearchComponent {
    readonly isCreateTagLoading$: Observable<boolean> = this.loadingService.getIsLoading(CREATE_TAG_LOADING_TAG);
    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);

    search: SaveSearchModel;
    errorMessage: string;
    header = '';
    searchCriteria: UniversalSearchRequestModelInterface;
    isContact: string = '';
    isSearchAlreadyConverted: boolean = false;
    isCreateAutomationEnabled: boolean = false;
    tagList$: Observable<TagsDto[]>;
    isSavedSearchCountsExceedLimit: boolean = false;
    readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;

    constructor(
        private savedSearchService: SavedSearches,
        private toastr: ToastrService,
        private commonUtilService: CommonUtilService,
        private requestbodyService: RequestBodyService,
        private listsService: Lists,
        private readonly router: Router,
        private readonly loadingService: LoadingService,
        private readonly saveSearchApiService: SaveSearchApiService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<SaveSearchComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data: SaveSearchDialogData,
        private readonly tagsApiService: TagsApiService,
        private readonly tagService: TagService,
        private readonly searchService: SearchService,
        private readonly featureFlagService: FeatureFlagService,
    ) {
        this.search = new SaveSearchModel();
        if (this.data.header) {
            this.header = this.data.header;
        }
        if (this.data.type.toLowerCase() === 'universal') {
            this.isContact = this.data.searchCriteria?.request_type?.toLowerCase() === 'contacts' ? 'contact' : 'company';
        } else {
            this.isContact = this.data.type.toLowerCase();
        }
        if (this.isContact) {
            this.search.type = this.isContact;
        }
        if (this.data.searchCriteria) {
            this.searchCriteria = this.data.searchCriteria;
            if (!this.searchCriteria.accuracy_tier_contact) {
                this.searchCriteria.accuracy_tier_contact = 'EV';
            }
            if (!this.searchCriteria.accuracy_tier_company) {
                this.searchCriteria.accuracy_tier_company = 'MV';
            }
        }
        this.setTagsList();

        const savedSearchCountsLimit = environment.icpAutomationSavedSearchCountsLimit;
        this.isSavedSearchCountsExceedLimit = (this.searchService.universalCount.companies_count >= savedSearchCountsLimit) || (this.searchService.universalCount.contacts_count >= savedSearchCountsLimit);

    }

    createTag(tagName: string): void {
        const createObj: SaveTagDto = {
            name: tagName
        };
        this.loadingService.loadingWrapper(this.tagsApiService.createTag(createObj), CREATE_TAG_LOADING_TAG).pipe(untilDestroyed(this)).subscribe((newTag: TagsDto) => {
            this.search = {
                ...this.search,
                tags: [...this.search.tags, newTag.id]
            }
            this.tagService.refreshList();
            this.toastr.success('Tag created successfully');
        });
    }

    onSaveSearch(): void {
        if (!this.validateSearchName()) {
            return;
        }

        this.submitSaveSearch();
    }

    onClose(): void {
        this.matDialogRef.close();
    }

    onIsPublicChange(value: boolean): void {
        if (!value) {
            this.isCreateAutomationEnabled = false;
        }
    }

    private saveSearchData(): void {
        if (this.search.type.toLowerCase() === 'contact') {
            this.savedSearchService.welComeLoadedFlag.next(false);
        } else {
            this.savedSearchService.welComeLoadedCompanyFlag.next(false);
        }

        this.searchCriteria.request_type = this.search.type === 'contact' ? 'CONTACTS' : 'COMPANIES';
        delete (<any>this.searchCriteria).selected_boolean_operator;
        const saveInfo: SaveSearchPayload = {
            ...this.search,
            tags: this.search.tags
        }
        this.loadingService.loadingWrapper(
            this.saveSearchApiService.createSearch(saveInfo, this.searchCriteria).pipe(
                tap((response: SavedSearchCreateUpdateResponse<UniversalSearchRequestModelInterface>) => {
                    if (this.search.type.toLowerCase() === 'contact') {
                        this.savedSearchService.currentSearchId = cloneDeep(response.id);
                        this.savedSearchService.currentSearchName = cloneDeep(response.searchName);
                        this.requestbodyService.changeContactSaveSearch(<SavedSearchCreateUpdateResponse<RequestBodyContact>>response);
                        this.savedSearchService.welComeLoadedFlag.next(false);
                    }
                    if (this.search.type.toLowerCase() === 'company') {
                        this.savedSearchService.currentSearchId = cloneDeep(response.id);
                        this.savedSearchService.currentSearchName = cloneDeep(response.searchName);
                        this.requestbodyService.changeCompanySaveSearch(<SavedSearchCreateUpdateResponse<RequestBodyCompany>>response);
                        this.savedSearchService.welComeLoadedCompanyFlag.next(false);
                    }
                    this.savedSearchService.refreshList();
                    this.toastr.success('Search saved successfully');
                }),
                switchMap((response: SavedSearchCreateUpdateResponse<UniversalSearchRequestModelInterface>) => {
                    if (!this.isCreateAutomationEnabled) {
                        return of(null);
                    }
                    return this.featureFlagService.getFlag(FeatureFlagsEnum.GTMCanvas).pipe(
                        take(1),
                        tap((enabled: boolean) => {
                            if (enabled) {
                                this.redirectToGtmCanvas(response.id);
                            } else {
                                this.redirectToIntentSettings(response.id);
                            }
                        })
                    );
                })
            ),
            LOADING_TAG
        ).pipe(untilDestroyed(this)).subscribe(
            () => this.onClose(),
            () => {
                this.savedSearchService.welComeLoadedFlag.next(false);
                this.savedSearchService.welComeLoadedCompanyFlag.next(false);
            }
        );
    }

    private checkPrivateLists(): void {
        const privateListIds = this.listsService.listRowData.filter((list) => {
            if (!list.isPublic) {
                const listId = list.id;
                const isListIdInSearchCriteria = ['lists', 'contact_lists', 'suppression_lists'].some((key) => {
                    return this.searchCriteria[key]?.includes(listId);
                });
                return isListIdInSearchCriteria;
            }
            return false;
        }).map((list) => list.id);
        if (privateListIds.length) {
            const modalTextData: MatDialogConfig = {
                panelClass: 'modal-md',
                data: {
                    promptMessage: 'In order to save this search, all lists in your search need to be Team available. Clicking Save below will convert any Private lists in your search to Team lists.',
                    buttonCancelText: 'Cancel',
                    buttonConfirmText: 'Save',
                    type: 'increaseListPrivacy',
                    privateListIds
                }
            };
            this.matDialog.open(MakeListsPublicPromptComponent, modalTextData).afterClosed().pipe(untilDestroyed(this)).subscribe((result) => {
                this.isSearchAlreadyConverted = !!result?.success;
                if (!this.isSearchAlreadyConverted) {
                    return;
                }
                this.submitSaveSearch();
            });
        } else {
            this.saveSearchData();
        }
    }

    private submitSaveSearch(): void {
        this.savedSearchService.emitSaveUpdateSearchModalClosed();

        if (this.search.isPublic && !this.isSearchAlreadyConverted) {
            this.checkPrivateLists();
        } else {
            this.saveSearchData();
        }
    }

    private validateSearchName(): boolean {
        if (this.search.searchName.trim() === '') {
            this.commonUtilService.showToastWithError('Saved Search name is required');
            return false;
        }

        if (this.search.searchName.length > 100) {
            this.commonUtilService.showToastWithError('Saved Search name cannot be more than 100 characters.');
            return false;
        }

        return true;
    }

    private setTagsList(): void {
        this.tagList$ = this.tagService.getTagsList();
    }

    private redirectToIntentSettings(savedSearhId: number): void {
        this.router.navigate(['/', { outlets: { modal: [ModalNavigationEnum.ICP_AUTOMATIONS] } }], {
            queryParams: {
            [ICP_VERTICAL_TAB_PARAM]: VerticalMenuTabsEnum.ICP_AUTOMATIONS,
            [ICP_HORIZONTAL_TAB_PARAM]: PredictiveIntentAutomationEnum.NEW_AUTOMATION,
            [ICP_AUTOMATION_SAVED_SEARCH_ID]: savedSearhId,
            },
            queryParamsHandling: 'merge'
        });
    }

    private redirectToGtmCanvas(savedSearchId: number): void {
        this.router.navigate([ResultTabsetUrlsEnum.AGENTIC_WORKFLOWS], {
            queryParams: {
                [AW_SELECTED_SIGNAL_PARAM]: ICP_SIGNAL_KEY,
                [AW_SELECTED_SAVED_SEARCH_PARAM]: savedSearchId,
                [AW_START_CONSTRUCTOR_PARAM]: 1,
            },
            queryParamsHandling: 'merge'
        });
    }

}
